import { Box } from '@mui/material'
import type { EmbedReference } from '@shared/ast/astTypes'
import { inlineEmbedContent } from '@shared/ast/embedReference'
import type { NodeKey } from 'lexical'
import { useContext } from 'react'

import { useEmbedActions } from './EmbedActionsMenu'
import { EmbedContext, embedStatusText, renderInlineEmbedContent } from './SourceEmbedView'
import { useSourceEmbed } from './useSourceEmbed'

export function InlineSourceEmbedView({ source, nodeKey }: { source: EmbedReference; nodeKey: NodeKey }) {
  const context = useContext(EmbedContext)
  const { result, failed } = useSourceEmbed(context.bookId, source)
  const actions = useEmbedActions({ ...context, source, nodeKey, result, failed, presentation: 'inline' })
  const content = result?.status === 'resolved' ? inlineEmbedContent(result.content) : undefined
  const status = failed ? 'error' : result?.status === 'resolved' && !content ? 'missing' : result?.status || 'loading'
  const message =
    failed || !context.bookId
      ? 'לא ניתן לטעון את המקור כעת'
      : !result
        ? 'טוען את המקור…'
        : result.status !== 'resolved'
          ? embedStatusText[result.status]
          : 'המקור כולל כמה פסקאות — אפשר להציג אותו ככרטיס'

  return (
    <>
      <Box
        component="span"
        contentEditable={false}
        data-embed-status={status}
        role={context.isAuthor ? 'button' : undefined}
        tabIndex={context.isAuthor ? 0 : undefined}
        aria-haspopup={context.isAuthor ? 'menu' : undefined}
        aria-expanded={context.isAuthor ? actions.expanded : undefined}
        title={context.isAuthor ? 'טקסט משובץ — פעולות' : undefined}
        onClick={context.isAuthor ? actions.open : undefined}
        onKeyDown={context.isAuthor ? actions.onKeyDown : undefined}
        sx={{
          display: 'inline',
          bgcolor: 'action.hover',
          borderRadius: '3px',
          boxDecorationBreak: 'clone',
          WebkitBoxDecorationBreak: 'clone',
          px: '0.15em',
          py: '0.06em',
          cursor: context.isAuthor ? 'pointer' : 'inherit',
          whiteSpace: 'pre-wrap',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        {content && !failed ? renderInlineEmbedContent(content) : message}
      </Box>
      {actions.menu}
    </>
  )
}
